import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Briefcase, Building2, CheckCircle2, XCircle,
  AlertOctagon, GitCompareArrows, HelpCircle, FileSearch, Layers,
  ShieldCheck, Bell, Compass, ClipboardCheck, ClipboardList,
  Presentation, Upload,
} from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { setPathway, withPathway } from "@/lib/pathway";

/**
 * /sample — PUBLIC, static guided sample.
 *
 * Purpose: give a first-time client a genuinely-accessible walkthrough of
 * either pathway WITHOUT registering, verifying an email, or uploading any
 * confidential documents. All content on this page is static fictional data.
 * Interactive actions (approve, revise, export) are represented visually
 * and marked "Preview only — enter the Test Workspace to try".
 *
 * The audience is chosen via `?path=investor|company` or the persisted
 * pathway. If neither is available, an audience picker is shown.
 */

const INVESTOR_SAMPLE = {
  opp_name: "Corvatec Series B",
  amount: "€20M",
  stage: "Series B lead · Impact-linked",
  central_question: "Should the family office invest €20M in Corvatec's Series B round?",
  thesis: "Contracted anchor-tenant revenue, independently verified emissions reduction, defensible unit economics against the two largest incumbents.",
  material_issues: [
    { id: "01", title: "Hardware gross-margin contradiction",
      body: "Pitch deck states 42% blended gross margin; Q3 financial model implies 31% at current mix. Unresolved.",
      status: "Contradiction" },
    { id: "02", title: "Anchor customer concentration",
      body: "Top three utility customers represent 68% of contracted ARR through 2028. Churn scenarios not modelled.",
      status: "Elevated" },
    { id: "03", title: "Scope 3 emissions methodology",
      body: "LCA report references GHG Protocol 2013 boundaries. Investor covenant requires 2023 boundaries.",
      status: "Requires review" },
  ],
  passport_sections: [
    { label: "Original thesis", body: "Contracted anchor revenue, verified emissions reduction, defensible unit economics." },
    { label: "Recorded committee decision",
      body: "Approve, subject to two conditions: (1) reconciled hardware margin walk; (2) refreshed LCA under GHG Protocol 2023 boundaries. This decision was entered by the human investment committee; ImpactVest does not issue investment recommendations." },
  ],
  approval_conditions: [
    "Signed reconciliation of hardware gross margin between pitch materials and Q3 model.",
    "Refreshed Life-Cycle Assessment under GHG Protocol 2023 boundaries, independent assurance.",
    "Top-three customer stress test: quarterly ARR review with named monitoring owner.",
  ],
  monitoring: [
    { trigger: "Any single anchor customer contributes >40% of trailing 12-month ARR.", cond: "Top-three customer stress test" },
    { trigger: "Independent LCA delivered >90 days after committed date.", cond: "Refreshed LCA (2023 boundaries)" },
    { trigger: "Quarterly gross margin deviates >6ppt from reconciled walk.", cond: "Gross-margin reconciliation" },
  ],
};

const COMPANY_SAMPLE = {
  company_name: "Terravise Nutrition Ltd",
  round: "Series A · Food-tech · EU",
  headline_gap: "3 release blockers · 6 readiness actions",
  claim_signals: [
    { icon: CheckCircle2, tone: "supported", label: "Supported claims", count: 4,
      example: '"Certified organic supply chain since 2022" · backed by third-party certificate PDF + audit report' },
    { icon: AlertOctagon, tone: "partial", label: "Partially supported claims", count: 3,
      example: '"15-20% farmer income uplift" · outcome measured, but methodology and baseline missing' },
    { icon: XCircle, tone: "unsupported", label: "Unsupported claims", count: 2,
      example: '"100k households reached" · no corroborating source in uploaded documents' },
    { icon: GitCompareArrows, tone: "conflict", label: "Conflicting evidence", count: 1,
      example: '"30% carbon reduction" · pitch deck vs LCA report differ by 12ppt' },
    { icon: HelpCircle, tone: "insufficient", label: "Insufficient evidence", count: 2,
      example: '"Scope 3 emissions reduction" · evidence exists but boundary and reporting period unclear' },
  ],
  readiness_actions: [
    { p: "P0", action: "Provide methodology and baseline for farmer income uplift claim before diligence.",
      why: "Investors will not accept an outcome claim without a defined baseline and measurement methodology." },
    { p: "P0", action: "Reconcile carbon-reduction figure between pitch deck and LCA report.",
      why: "Conflicting numbers in the same pack materially reduce diligence confidence." },
    { p: "P0", action: "Remove or substantiate the '100k households reached' claim.",
      why: "No corroborating evidence is present in the uploaded documents." },
    { p: "P1", action: "Refresh Scope 3 boundary declaration to GHG Protocol 2023.",
      why: "Anchor-investor covenant explicitly references the 2023 boundaries." },
    { p: "P1", action: "Add third-party assurance letter for organic supply-chain certification.",
      why: "Strengthens an already-supported claim. The company decides whether to obtain assurance based on its own diligence expectations; ImpactVest does not set a market threshold." },
    { p: "P2", action: "Consolidate KPI dictionary (definition · baseline · boundary · methodology · reporting period · owner) for all impact KPIs.",
      why: "Reduces the number of clarification questions investors typically ask during diligence by making every KPI's construction explicit up front." },
  ],
  framework_alignment: [
    { framework: "ESRS E1 (Climate)", status: "Mapped · gaps flagged" },
    { framework: "IFRS S2", status: "Mapped · gaps flagged" },
    { framework: "EU Taxonomy · Substantial contribution",       status: "Requires professional interpretation" },
    { framework: "SFDR Article 9",  status: "Outside scope (fund-level, not company-level)" },
  ],
};


export default function PublicSample() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // P0 pathway routing: URL parameter is the ONLY source of truth. A
  // missing/invalid ?path= must render the neutral picker even if the
  // user previously chose a pathway on another page. Session storage
  // captures the choice so authenticated downstream routes can recognise
  // intent, but never silently populates this page.
  const urlPath = params.get("path");
  const initial = (urlPath === "company" || urlPath === "investor") ? urlPath : null;
  const [pathway, setPathwayState] = useState(initial);
  useEffect(() => {
    const q = params.get("path");
    setPathwayState((q === "investor" || q === "company") ? q : null);
  }, [params]);
  useEffect(() => { if (pathway) setPathway(pathway); }, [pathway]);

  const choose = (p) => {
    setPathwayState(p);
    navigate(`/sample?path=${p}`, { replace: true });
  };
  const backToPicker = () => {
    setPathwayState(null);
    navigate("/sample", { replace: true });
  };

  useEffect(() => {
    document.title = pathway === "company"
      ? "ImpactVest · Fictional evidence pack (guided sample)"
      : "ImpactVest · Fictional investment decision (guided sample)";
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathway]);

  if (!pathway) {
    return (
      <div>
        <Header />
        <main className="pt-28 md:pt-36 pb-24 iv-container" data-testid="sample-pathway-picker">
          <div className="iv-eyebrow mb-8">Choose your pathway</div>
          <h1 className="font-serif-display text-[42px] md:text-[60px] leading-[1.0] tracking-tight text-iv-fg max-w-3xl">
            One evidence standard.<br />Two distinct mandates.
          </h1>
          <p className="mt-6 text-[15.5px] text-iv-fg-2 max-w-2xl leading-relaxed">
            ImpactVest serves investors evaluating opportunities and companies preparing evidence
            for diligence. Choose the pathway that reflects your role.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
            <button data-testid="sample-choose-investor"
              aria-label="For investors — Evaluate an opportunity"
              onClick={() => choose("investor")}
              className="iv-path-card">
              <div className="flex items-center gap-2 iv-path-eyebrow">
                <Briefcase size={13} strokeWidth={1.7} />For investors
              </div>
              <div className="iv-path-title">Evaluate an opportunity</div>
              <div className="iv-path-sub">Examine the evidence. Preserve the reasoning behind the decision.</div>
              <div className="iv-path-arrow"><ArrowRight size={16} strokeWidth={1.8} /></div>
            </button>
            <button data-testid="sample-choose-company"
              aria-label="For companies — Build an evidence pack"
              onClick={() => choose("company")}
              className="iv-path-card">
              <div className="flex items-center gap-2 iv-path-eyebrow">
                <Building2 size={13} strokeWidth={1.7} />For companies
              </div>
              <div className="iv-path-title">Build an evidence pack</div>
              <div className="iv-path-sub">Substantiate your claims. Prepare for investor diligence.</div>
              <div className="iv-path-arrow"><ArrowRight size={16} strokeWidth={1.8} /></div>
            </button>
          </div>
          <p className="mt-8 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
            Fictional data · No confidential materials required
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return pathway === "company"
    ? <CompanySample onSwitch={backToPicker} />
    : <InvestorSample onSwitch={backToPicker} />;
}


function SampleFrame({ children, pathwayLabel, onSwitch, pathwayIcon }) {
  return (
    <div data-testid="sample-frame">
      <Header />
      <div className="border-b border-iv-line bg-iv-bg-2">
        <div className="iv-container flex items-center justify-between h-[42px] flex-wrap gap-2">
          <span className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-iv-gold">
            Guided sample · Fictional data · No account required
          </span>
          <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-iv-fg-3 flex items-center gap-3">
            {pathwayIcon} {pathwayLabel}
            <button onClick={onSwitch} className="ml-3 iv-link-underline text-iv-fg-2">Change pathway</button>
          </span>
        </div>
      </div>
      <main className="pt-16 md:pt-20 pb-20">{children}</main>
      <Footer />
    </div>
  );
}


function InvestorSample({ onSwitch }) {
  const s = INVESTOR_SAMPLE;
  return (
    <SampleFrame
      pathwayLabel="For investors"
      onSwitch={onSwitch}
      pathwayIcon={<Briefcase size={12} strokeWidth={1.7} />}
    >
      <div className="iv-container">
        <div className="iv-eyebrow mb-6">Guided sample · Investor pathway</div>
        <h1 className="font-serif-display text-[36px] md:text-[54px] leading-[1.02] tracking-tight text-iv-fg max-w-4xl">
          {s.central_question}
        </h1>

        {/* Decision panel */}
        <Reveal className="iv-canvas rounded-[3px] mt-10 px-6 md:px-10 py-8" data-testid="sample-decision">
          <div className="iv-canvas-eyebrow mb-3">Decision brief</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-2">
            <Meta label="Amount" value={s.amount} />
            <Meta label="Stage" value={s.stage} />
            <Meta label="Decision" value="2 weeks" />
            <Meta label="Hold" value="7–10 years" />
          </div>
          <div className="mt-8 border-l-2 border-[var(--ivc-forest)] pl-6 py-2">
            <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-forest)]">Original thesis</div>
            <div className="font-serif-display text-[20px] leading-snug mt-2 text-[var(--ivc-ivory)]">{s.thesis}</div>
          </div>
        </Reveal>

        {/* Material issues */}
        <Reveal className="iv-canvas rounded-[3px] mt-4 px-6 md:px-10 py-8" data-testid="sample-material-issues">
          <div className="flex items-baseline justify-between mb-5">
            <div className="iv-canvas-eyebrow">Three material issues</div>
            <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">Ordered by significance</span>
          </div>
          <ul>
            {s.material_issues.map((mi) => (
              <li key={mi.id} className="grid grid-cols-[46px_1fr_auto] items-start gap-5 py-4 border-b border-[var(--ivc-line)]">
                <span className="font-mono text-[11px] tracking-[0.18em] text-[var(--ivc-gold-2)] mt-1.5">{mi.id}</span>
                <div>
                  <div className="font-serif-display text-[18px] leading-tight text-[var(--ivc-ivory)]">{mi.title}</div>
                  <p className="text-[13.5px] text-[var(--ivc-ivory-2)] mt-1.5 max-w-2xl leading-relaxed">{mi.body}</p>
                  <div className="mt-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-amber)]">{mi.status}</div>
                </div>
                <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] self-center">Preview only</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Passport */}
        <Reveal className="iv-canvas rounded-[3px] mt-4 px-6 md:px-10 py-8" data-testid="sample-passport">
          <div className="iv-canvas-eyebrow mb-3">Decision Passport · Committee Approved · v1</div>
          <h3 className="font-serif-display text-[26px] leading-tight text-[var(--ivc-ivory)]">{s.opp_name}</h3>
          <p className="mt-3 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-gold-2)] max-w-2xl leading-relaxed">
            ImpactVest does not recommend investments. Every entry below is a human committee record; the platform preserves it against its evidence.
          </p>
          {s.passport_sections.map((sec) => (
            <div key={sec.label} className="mt-5 pt-4 border-t border-[var(--ivc-line)]">
              <div className="iv-canvas-eyebrow mb-2">{sec.label}</div>
              <p className="font-serif-display text-[16px] leading-relaxed text-[var(--ivc-ivory)]">{sec.body}</p>
            </div>
          ))}
          <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]">
            <div className="iv-canvas-eyebrow mb-3">Approval conditions</div>
            <ol className="space-y-2 list-decimal ml-5">
              {s.approval_conditions.map((c, i) => (
                <li key={i} className="text-[14px] text-[var(--ivc-ivory)] leading-snug">{c}</li>
              ))}
            </ol>
          </div>
          <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]">
            <div className="iv-canvas-eyebrow mb-3">Reconsideration triggers</div>
            <ul className="space-y-3">
              {s.monitoring.map((m, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <Bell size={16} className="text-[var(--ivc-amber)] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[14px] text-[var(--ivc-ivory)] leading-snug">{m.trigger}</p>
                    <p className="mt-1 font-mono text-[10.5px] tracking-[0.10em] uppercase text-[var(--ivc-gold-2)]">Linked to condition: {m.cond}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <UpgradeBand pathway="investor" />
      </div>
    </SampleFrame>
  );
}


function CompanySample({ onSwitch }) {
  const s = COMPANY_SAMPLE;
  return (
    <SampleFrame
      pathwayLabel="For companies"
      onSwitch={onSwitch}
      pathwayIcon={<Building2 size={12} strokeWidth={1.7} />}
    >
      <div className="iv-container">
        <div className="iv-eyebrow mb-6">Guided sample · Company pathway</div>
        <h1 className="font-serif-display text-[36px] md:text-[54px] leading-[1.02] tracking-tight text-iv-fg max-w-4xl">
          {s.company_name} · Investor Evidence Pack
        </h1>
        <p className="mt-4 font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-3">
          {s.round} · {s.headline_gap}
        </p>

        {/* Claim signals */}
        <Reveal className="iv-canvas rounded-[3px] mt-10 px-6 md:px-10 py-8" data-testid="sample-claim-signals">
          <div className="iv-canvas-eyebrow mb-6">Claim status · per material claim</div>
          <ul>
            {s.claim_signals.map((c) => {
              const Icon = c.icon;
              return (
                <li key={c.tone} className="grid grid-cols-[40px_180px_60px_1fr] items-start gap-4 py-4 border-b border-[var(--ivc-line)]">
                  <Icon size={20} className="text-[var(--ivc-gold-2)] mt-0.5" strokeWidth={1.5} />
                  <div className="font-serif-display text-[16px] leading-tight text-[var(--ivc-ivory)]">{c.label}</div>
                  <div className="font-mono text-[13px] tracking-[0.08em] text-[var(--ivc-gold-2)]">{c.count}</div>
                  <p className="text-[13.5px] text-[var(--ivc-ivory-2)] leading-relaxed">{c.example}</p>
                </li>
              );
            })}
          </ul>
        </Reveal>

        {/* Readiness actions */}
        <Reveal className="iv-canvas rounded-[3px] mt-4 px-6 md:px-10 py-8" data-testid="sample-readiness">
          <div className="iv-canvas-eyebrow mb-4">Investor-readiness actions · prioritised</div>
          <ul>
            {s.readiness_actions.map((a, i) => (
              <li key={i} className="grid grid-cols-[90px_1fr] items-start gap-4 py-4 border-b border-[var(--ivc-line)]">
                <span className={`font-mono text-[11px] tracking-[0.18em] uppercase mt-1
                  ${a.p === "P0" ? "text-[var(--ivc-amber)]" : a.p === "P1" ? "text-[var(--ivc-gold-2)]" : "text-[var(--ivc-ivory-3)]"}`}>
                  {a.p === "P0" ? "Critical" : a.p === "P1" ? "Advised" : "Optional"}
                </span>
                <div>
                  <p className="text-[14.5px] text-[var(--ivc-ivory)] leading-snug">{a.action}</p>
                  <p className="mt-1.5 text-[12.5px] text-[var(--ivc-ivory-3)] leading-relaxed">Why: {a.why}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Framework alignment */}
        <Reveal className="iv-canvas rounded-[3px] mt-4 px-6 md:px-10 py-8" data-testid="sample-framework">
          <div className="iv-canvas-eyebrow mb-4">Framework alignment · evidence organisation only, never certification</div>
          <ul>
            {s.framework_alignment.map((f, i) => (
              <li key={i} className="grid grid-cols-[1fr_auto] items-baseline gap-4 py-3 border-b border-[var(--ivc-line)]">
                <span className="font-serif-display text-[15.5px] text-[var(--ivc-ivory)]">{f.framework}</span>
                <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)]">{f.status}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12.5px] font-mono tracking-[0.12em] uppercase text-[var(--ivc-ivory-3)]">
            ImpactVest never marks claims as certified, assured or verified. Framework mapping is presented as evidence organisation only.
          </p>
        </Reveal>

        <UpgradeBand pathway="company" />
      </div>
    </SampleFrame>
  );
}


function UpgradeBand({ pathway }) {
  const isCompany = pathway === "company";
  return (
    <div className="mt-10 iv-canvas rounded-[3px] px-6 md:px-10 py-8" data-testid="sample-upgrade-band">
      <div className="iv-canvas-eyebrow mb-3">Continue with your own materials</div>
      <h3 className="font-serif-display text-[26px] md:text-[34px] leading-tight text-[var(--ivc-ivory)] max-w-3xl">
        {isCompany
          ? "Test with your own evidence in a private workspace."
          : "Test with your own opportunity in a private workspace."}
      </h3>
      <p className="mt-4 text-[14px] leading-relaxed text-[var(--ivc-ivory-2)] max-w-2xl">
        The Test Workspace uses a single-use email link. No password. No confidential documents required.
        Everything you upload stays private to you.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to={withPathway("/access", pathway)}
          data-testid="sample-upgrade-cta"
          className="iv-btn iv-btn--primary"
        >
          Enter the Test Workspace <ArrowRight size={16} />
        </Link>
        <Link
          to={isCompany ? "/companies" : "/investors"}
          className="iv-btn"
        >
          Read the pathway overview <ArrowUpRight size={16} />
        </Link>
      </div>
    </div>
  );
}


function Meta({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">{label}</div>
      <div className="text-[14px] text-[var(--ivc-ivory)] mt-1">{value}</div>
    </div>
  );
}
